import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { applyActionCode, getAuth } from "firebase/auth";
import { Button } from "src/components/ui/button";
import { Link } from "@tanstack/react-router";
import LoadingOverlay from "@/components/LoadingOverlay";
import { Check, X } from "lucide-react";

export const Route = createFileRoute("/_auth/verify-email")({
  component: VerifyEmailPage,
  staticData: {
    title: "Verify Email",
  },
});

function VerifyEmailPage() {
  const [verificationState, setVerificationState] = useState<
    "loading" | "success" | "error"
  >("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const auth = getAuth();
        const urlParams = new URLSearchParams(window.location.search);
        const oobCode = urlParams.get("oobCode");

        if (!oobCode) {
          setVerificationState("error");
          setErrorMessage("Invalid verification link. Please try again.");
          return;
        }

        // Check if the user is already signed in
        if (auth.currentUser) {
          try {
            await applyActionCode(auth, oobCode);

            // Force refresh of the user's token to ensure emailVerified flag is updated
            await auth.currentUser.getIdToken(true);

            setVerificationState("success");
            auth.currentUser?.reload();
          } catch (error) {
            // If the code has already been applied, just show success
            // This handles the case where the page is refreshed after verification
            setVerificationState("success");
            auth.currentUser?.reload();
          }
        }
      } catch (error) {
        console.error("Unexpected error during verification:", error);
        setVerificationState("error");
        setErrorMessage(
          "An unexpected error occurred. Please try again later.",
        );
        getAuth().currentUser?.reload();
      }
    };

    verifyEmail();
  }, []);

  if (verificationState === "loading") {
    return <LoadingOverlay isLoading={true} />;
  }

  return (
    <div className="flex flex-col items-center justify-center gap-6 p-6 text-center">
      {verificationState === "success" && (
        <>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10 text-success dark:bg-success/20 dark:text-success">
            <Check className="h-8 w-8" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-semibold">
            Email Verified Successfully! 🎉
          </h1>
          <p className="text-muted-foreground">
            Awesome! Your account is now fully activated and ready to go. You're
            all set to start your trading journey!
          </p>
          <p className="text-muted-foreground">You may now close this page.</p>
        </>
      )}

      {verificationState === "error" && (
        <>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive dark:bg-destructive dark:text-destructive">
            <X className="h-8 w-8" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-semibold">
            Oops! Verification Didn't Work
          </h1>
          <p className="text-muted-foreground">
            {errorMessage} Don't worry though, we can try again!
          </p>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row">
            <Link to="/signin">
              <Button variant="outline">Back to Sign In</Button>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
